import { createClient } from "@supabase/supabase-js"
import { executeOrQueueFunction } from "@/lib/offlineQueue"

export const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string
export const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
})

export type ApiError = {
  message: string
  code?: string | number
  status?: number
  details?: unknown
  hint?: string
  issues?: { path: string; message: string; code?: string }[]
  source?: string
}

export async function api<T = unknown>(
  action: string,
  input?: unknown,
): Promise<{ data: T | null; error: ApiError | null }> {
  const session = (await supabase.auth.getSession()).data.session

  // SECURITY: Fail fast if no session - don't send unauthenticated requests
  if (!session?.access_token) {
    return {
      data: null,
      error: {
        message: "Not authenticated",
        code: "UNAUTHENTICATED",
        status: 401,
      },
    }
  }

  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/api`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ action, input }),
  })

  let json: unknown = null
  try {
    json = await res.json()
  } catch {
    return {
      data: null,
      error: { message: "Invalid JSON from API", status: res.status },
    }
  }

  if (!res.ok || (json !== null && typeof json === "object" && "ok" in json && json.ok === false)) {
    const err: ApiError =
      json !== null && typeof json === "object" && "error" in json && typeof json.error === "string"
        ? { message: json.error, status: res.status }
        : json !== null && typeof json === "object" && "error" in json
          ? { message: "API error", ...(json.error as Record<string, unknown>), status: res.status }
          : { message: "Unknown error", status: res.status }
    return { data: null, error: err }
  }

  return {
    data: json !== null && typeof json === "object" && "data" in json ? (json.data as T) : null,
    error: null,
  }
}

export async function apiQueued<T = unknown>(
  action: string,
  input?: unknown,
): Promise<{ data: T | null; error: ApiError | null } | null> {
  const res = await executeOrQueueFunction<T>(action, input)
  if (res === null) return null
  // normalize to ApiError type - error is already properly typed from executeOrQueueFunction
  return { data: res.data, error: res.error as ApiError | null }
}

export async function getAccessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession()
  return data.session?.access_token ?? null
}
